import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';
import ThemeToggle from '../shared/ThemeToggle';
import logo from '../../assets/logos/logo.png';

/**
 * Πλαίσιο του super admin στη νέα εμφάνιση (14/10/2026): κεφαλίδα (ποιος είναι συνδεδεμένος, θέμα,
 * αποσύνδεση) και πλαϊνό μενού σε ομάδες — το ίδιο μοτίβο και οι ίδιες κλάσεις με τη διαχείριση
 * καταστήματος (StoreAdminDashboard + shared/StoreAdminLayout + shared/StoreNavigation). Εκείνα δεν
 * αλλάζουν: τα χρησιμοποιεί το κατάστημα, με δικούς του κανόνες ορατότητας. Ως τότε οι εννέα καρτέλες
 * ήταν οριζόντιες στην κεφαλίδα, με κυανό χρώμα, και δεν χωρούσαν.
 *
 * Παρουσίαση μόνο: ποιες σελίδες υπάρχουν και τι κάνει το κλικ το αποφασίζει το SuperAdminPanel.
 * Στοιχείο με `action` ανοίγει παράθυρο (π.χ. «Κλειδιά πρόσβασης») αντί για σελίδα.
 *
 * Η κλάση xv2-store-nav στο μενού είναι σκόπιμη: πάνω της κλειδώνουν οι κανόνες του RoleLight.css που
 * βάζουν την κεφαλίδα, τα πεδία και τα παράθυρα στα tokens του θέματος (surface-2 αντί για #fff στο
 * «Χαρτί») — χωρίς αυτήν το ανοιχτό θέμα του super admin θα έβγαινε άλλο από του καταστήματος.
 */

export interface ShellItem {
  id: string;
  label: string;
  icon: React.ElementType;
  /** Ανοίγει παράθυρο αντί για σελίδα: χωρίς aria-current, και η τρέχουσα σελίδα μένει ίδια. */
  action?: () => void;
}

export interface ShellGroup {
  id: string;
  label: string;
  items: ShellItem[];
}

export interface SuperAdminShellProps {
  groups: ShellGroup[];
  /** Η σελίδα που φαίνεται (id ενός στοιχείου χωρίς action). */
  activeId: string;
  onSelect: (id: string) => void;
  /** Ποιος είναι συνδεδεμένος (όνομα) και με ποιον ρόλο. */
  userName: string;
  roleLabel: string;
  onLogout: () => void;
  children: React.ReactNode;
}

const SuperAdminShell: React.FC<SuperAdminShellProps> = ({
  groups, activeId, onSelect, userName, roleLabel, onLogout, children,
}) => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const sideNavRef = useRef<HTMLElement>(null);

  // Συρτάρι κινητού: κλείνει με κλικ έξω, με Esc, και όσο είναι ανοιχτό η σελίδα από πίσω δεν κυλά
  // (ίδια συμπεριφορά με το StoreNavigation).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) setMobileOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Το πλαϊνό μενού έχει δικό του scroll: η ενεργή επιλογή φέρνεται στο μέσο αν δεν φαίνεται.
  useEffect(() => {
    const nav = sideNavRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active || typeof active.getBoundingClientRect !== 'function') return;
    const navBox = nav.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    if (box.top < navBox.top || box.bottom > navBox.bottom) {
      nav.scrollTop += box.top - navBox.top - (navBox.height - box.height) / 2;
    }
  }, [activeId]);

  const allItems = groups.flatMap(group => group.items);
  const activeItem = allItems.find(item => item.id === activeId && !item.action);
  const ActiveIcon = activeItem?.icon;
  const logoutLabel = t('admin:logout');

  const choose = (item: ShellItem, closeDrawer: boolean) => {
    if (closeDrawer) setMobileOpen(false);
    if (item.action) item.action();
    else onSelect(item.id);
  };

  // Ίδια λίστα για το πλαϊνό μενού και το συρτάρι του κινητού· στο κινητό ψηλότερες γραμμές για το δάχτυλο.
  const renderGroups = (where: 'side' | 'drawer') => groups.filter(group => group.items.length > 0).map(group => {
    const headingId = `sa-nav-${where}-${group.id}`;
    return (
      <div key={group.id} role="group" aria-labelledby={headingId}>
        {/* role="heading" αντί για <h2>: το RoleLight.css αλλάζει το letter-spacing των h1–h3 */}
        <div id={headingId} role="heading" aria-level={2} className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-t4">
          {group.label}
        </div>
        <ul className="space-y-0.5">
          {group.items.map(item => {
            const Icon = item.icon;
            const isActive = !item.action && item.id === activeId;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  data-tab={item.id}
                  aria-current={isActive ? 'page' : undefined}
                  aria-haspopup={item.action ? 'dialog' : undefined}
                  onClick={() => choose(item, where === 'drawer')}
                  className={`w-full flex items-center gap-2.5 px-3 rounded-lg text-left text-sm transition-colors ${
                    where === 'side' ? 'h-8' : 'h-11'
                  } ${
                    isActive
                      ? 'bg-brand-soft text-brand font-semibold'
                      : 'text-t2 font-medium hover:bg-surface-3 hover:text-t1'
                  }`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand' : 'text-t3'}`} aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Κεφαλίδα: ίδια με της διαχείρισης καταστήματος — λογότυπο, θέμα, ποιος είναι συνδεδεμένος, αποσύνδεση */}
      <header className="role-header bg-surface-1 border-b border-line">
        <div className="w-full px-3 sm:px-4">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <img src={logo} alt="Xentrix Flow" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-base sm:text-lg font-bold text-t1 truncate">Xentrix Flow</p>
                <p className="text-xs text-t3 truncate">{t('superAdminPanel:header.scope')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <ThemeToggle />
              <div className="text-right hidden sm:block" data-testid="sa-signed-in">
                <p className="text-sm font-medium text-t1">{userName}</p>
                <p className="text-xs text-t3">{roleLabel}</p>
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="w-9 h-9 rounded-lg bg-surface-2 hover:bg-red-600/20 text-t3 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center transition-all active:scale-95"
                title={logoutLabel}
                aria-label={logoutLabel}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="md:flex">
        {/* Πλαϊνό μενού σε ομάδες (md+). Η στήλη τεντώνεται σε όλο το ύψος της σελίδας· το εσωτερικό μένει
            κολλημένο πάνω, με δικό του scroll, και χωρά κάτω από την κεφαλίδα (h-14 + 1px περίγραμμα). */}
        <aside className="xv2-store-nav hidden md:block w-60 shrink-0 bg-surface-2 border-r border-line">
          <div className="sticky top-0 flex flex-col h-[calc(100vh-3.5rem-1px)]">
            <nav ref={sideNavRef} aria-label={t('common:navigation')} className="flex-1 min-h-0 overflow-y-auto px-2 py-4 space-y-4">
              {renderGroups('side')}
            </nav>
          </div>
        </aside>

        {/* Κινητό: μπάρα με την τρέχουσα σελίδα + συρτάρι */}
        <div className="xv2-store-nav md:hidden bg-surface-2 border-b border-line sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 h-12">
            <div className="flex items-center gap-2 min-w-0">
              {ActiveIcon && <ActiveIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />}
              <span className="text-sm font-semibold text-t1 truncate">{activeItem?.label || t('common:menu')}</span>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t('common:navigation')}
              aria-expanded={mobileOpen}
              className="w-9 h-9 shrink-0 rounded-lg border border-line text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" />
            <div
              ref={drawerRef}
              className="absolute right-0 top-0 h-full w-72 max-w-[85vw] bg-surface-2 border-l border-line shadow-2xl flex flex-col"
              style={{ animation: 'saSlideInRight 0.2s ease-out' }}
              data-testid="sa-mobile-nav"
            >
              <div className="flex items-center justify-between gap-3 px-4 h-14 border-b border-line shrink-0">
                <div className="min-w-0">
                  <span className="block text-sm font-semibold text-t1">{t('common:navigation')}</span>
                  {/* Στο κινητό η κεφαλίδα δεν χωρά το όνομα: εδώ φαίνεται ποιος είναι συνδεδεμένος. */}
                  <span className="block text-xs text-t3 truncate">{t('superAdminPanel:header.signedInAs', { name: userName })}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label={t('common:close')}
                  className="w-9 h-9 shrink-0 rounded-lg text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <nav aria-label={t('common:navigation')} className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
                {renderGroups('drawer')}
              </nav>
            </div>
          </div>
        )}

        {/* Περιεχόμενο: ίδια περιθώρια με το StoreAdminLayout — οι σελίδες «Πίνακας»/«Φύλλο» δεν βάζουν δικά τους */}
        <main className="w-full min-w-0 flex-1 px-4 py-4 md:px-6 md:py-5">{children}</main>
      </div>

      <style>{`
        @keyframes saSlideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="sa-mobile-nav"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default SuperAdminShell;
